import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { Modal } from 'components/layouts/modal/modal'
import { FC, memo, useCallback, useLayoutEffect, useRef, useState } from 'react'
import { getImageData, tag as emojiTag } from '@crew/emoji-data'
import { useGetChatReactionsGroupByUserQuery } from '@crew/apis/chatReaction/chatReactionApis'
import { useTranslation } from '@crew/modules/dist/i18n'

type CrewChatMessageActionMenuReactionListDialogProps = {
  chatMessageId: string
  isOpen: boolean
  onClose: () => void
}

export const CrewChatMessageActionMenuReactionListDialog: FC<CrewChatMessageActionMenuReactionListDialogProps> =
  memo((props) => {
    const { t } = useTranslation()
    const iconRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const [maxHeight, setMaxHeight] = useState<number>(0)

    // メッセージに紐づくリアクションをユーザーごとにグループ化して取得
    // ダイアログが開いているときのみクエリを実行
    const { data: getChatReactionsGroupByUserResult } =
      useGetChatReactionsGroupByUserQuery(
        {
          chatMessageId: props.chatMessageId,
        },
        {
          skip: !props.isOpen, // ダイアログが閉じているときはクエリを実行しない
        }
      )

    useLayoutEffect(() => {
      const calculateHeight = () => {
        // 最初のアイコンとコンテナがない場合は何もしない
        if (!iconRef.current || !containerRef.current) return

        // 1. アイコン1つの高さを取得
        const iconHeight = iconRef.current.offsetHeight
        // 2. コンテナからgapの値を取得
        const gapValue = parseFloat(getComputedStyle(containerRef.current).gap)
        // 3. 2行分の高さを計算
        const twoLineHeight = iconHeight * 2 + gapValue
        setMaxHeight(twoLineHeight)
      }

      // データが読み込まれてから計算
      if (getChatReactionsGroupByUserResult?.userReactions?.length) {
        calculateHeight()
      }
    }, [getChatReactionsGroupByUserResult]) // リアクションデータが変わった場合に再計算

    // emojiのrender関数
    const renderEmoji = useCallback((emojiShortName: string) => {
      const customData = {
        [`data-${emojiTag.shortNameCustomDataAttributeName}`]: emojiShortName,
      }

      const validEmoji = emojiShortName && Boolean(getImageData(emojiShortName))

      const altText = validEmoji ? '' : '〓'

      const emojiClassName = validEmoji ? emojiTag.className : undefined

      return (
        <span className={emojiClassName} {...customData}>
          {altText}
        </span>
      )
    }, [])

    return (
      <Modal
        title={t('label.reactionList')}
        isOpen={props.isOpen}
        onClose={props.onClose}
      >
        <div className="flex flex-col gap-2.5 h-[500px] min-w-[480px] overflow-y-auto">
          {/** リアクション一覧 */}
          {getChatReactionsGroupByUserResult &&
            getChatReactionsGroupByUserResult.userReactions.map(
              (userReaction) => (
                <div
                  className="flex flex-row gap-2.5"
                  // ユーザーごとにグループ化しているので、userIdをキーに出来る
                  key={userReaction.user.id}
                >
                  {/** ユーザーアバター + ユーザー名 */}
                  <div className="flex flex-row gap-2.5 w-2/5 items-center">
                    <CrewUserAvatar
                      userId={userReaction.user.id}
                      displayName={userReaction.user.displayName}
                      size={CrewAvatarSize.xs}
                      cacheValue={
                        userReaction.user.id + userReaction.user.version
                      }
                    />
                    <div className="break-all">
                      {userReaction.user.displayName}
                    </div>
                  </div>
                  {/** リアクション絵文字一覧 */}
                  <div
                    className="flex flex-row gap-1.5 w-3/5 flex-wrap items-center overflow-hidden"
                    style={{
                      maxHeight: maxHeight > 0 ? `${maxHeight}px` : undefined,
                    }}
                    ref={containerRef}
                  >
                    {userReaction.reactions.map((reaction, idx) => (
                      <div
                        key={reaction.id}
                        // アイコン1つ辺りの高さを取得できれば良いので、1つ目のみ設定
                        ref={idx === 0 ? iconRef : null}
                        // 基本的にはcrew-reactionと同じクラス
                        // hover時の動作が不要なため、あえて直接定義している
                        className="rounded-lg px-2 py-0.5 ring-1 ring-inset ring-crew-gray-200 dark:ring-crew-gray-800"
                      >
                        {renderEmoji(reaction.reaction)}
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
        </div>
      </Modal>
    )
  })
